import Decimal from "decimal.js";
import React, {FC, useEffect, useState} from "react";
import {ValueNumber, ValueNumberType} from "@types";

export type ValueNumberInputProps = {
    id?: string;
    supported: Array<ValueNumberType>;
    defaultValue?: ValueNumber;
    value?: ValueNumber;
    minValue?: Decimal;
    maxValue?: Decimal;
    onChange?: (value: ValueNumber) => void;
    hideValueSelect?: boolean;
    placeholder?: string;
};

export const ValueNumberInput: FC<ValueNumberInputProps> = ({
                                                                id,
                                                                supported,
                                                                value,
                                                                minValue,
                                                                maxValue,
                                                                defaultValue,
                                                                onChange,
                                                                hideValueSelect,
                                                                placeholder,
                                                            }) => {
    const [selectedType, setSelectedType] = useState<ValueNumberType>(
        value?.type || defaultValue?.type || supported[0]
    );

    const [rawInput, setRawInput] = useState<string | undefined>(() => {
        if (value?.value && !value.value.isNaN()) return value.value.toString();
        if (defaultValue?.value && !defaultValue.value.isNaN()) return defaultValue.value.toString();
        return '';
    });

    const [inputValue, setInputValue] = useState<Decimal | undefined>(() => {
        if (value?.value && !value.value.isNaN()) return value.value;
        if (defaultValue?.value && !defaultValue.value.isNaN()) return defaultValue.value;
        return undefined;
    });

    useEffect(() => {
        if (value?.value && !value.value.isNaN()) {
            setSelectedType(value.type);
            setInputValue(value.value);
        }
    }, [value]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;

        setRawInput(raw);

        if (
            raw === '' ||
            raw === '-' ||
            raw.endsWith('.') ||
            /^-?\d+\.$/.test(raw)
        ) {
            return;
        }

        let decimalValue = new Decimal(raw);

        if (decimalValue.isNaN()) {
            setInputValue(undefined);
            onChange?.({type: selectedType, value: new Decimal(NaN)});
        }

        if (selectedType === 'COUNT') {
            decimalValue = decimalValue.trunc();
            setRawInput(decimalValue.toString());
        }

        if (minValue !== undefined && decimalValue.lessThan(minValue)) {
            decimalValue = minValue;
            setRawInput(decimalValue.toString());
        }

        if (maxValue !== undefined && decimalValue.greaterThan(maxValue)) {
            decimalValue = maxValue;
            setRawInput(decimalValue.toString());
        }

        setInputValue(decimalValue);
        onChange?.({type: selectedType, value: decimalValue});
    };

    const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newType = e.target.value as ValueNumberType;
        setSelectedType(newType);

        let adjustedValue = inputValue;
        if (newType === 'COUNT' && adjustedValue !== undefined) {
            adjustedValue = adjustedValue.trunc();
            setInputValue(adjustedValue);
        }

        onChange?.({
            type: newType,
            value: adjustedValue ?? new Decimal(NaN),
        });
    };

    const options = [
        {value: 'COUNT', label: 'Count'},
        {value: 'PERCENT', label: '%'},
        {value: 'SOL', label: 'SOL'},
        {value: 'QUOTE', label: 'SOL'},
        {value: 'USD', label: 'USD'},
    ]
        .filter(opt => supported.includes(opt.value as ValueNumberType))
        .map(opt => (
            <option key={opt.value} value={opt.value}>
                {opt.label}
            </option>
        ));

    if (options.length === 0) return null;

    return (
        <div id={id} className="flex items-center space-x-2">
            <input
                type="text"
                value={rawInput}
                onChange={handleInputChange}
                className="border p-2 w-full rounded"
                placeholder={placeholder || "Enter value"}
            />
            {!hideValueSelect && (
                <select
                    value={selectedType}
                    onChange={handleTypeChange}
                    className="border p-2 rounded bg-white"
                    disabled={options.length < 2}
                >
                    {options}
                </select>
            )}
        </div>
    );
};
